import { BaseInput } from '@/src/components'
import { appColors } from '@/src/theme/foundations/colors'
import { Box } from '@chakra-ui/react'
import { forwardRef } from 'react'
import type { ReactNode } from 'react'
import { FaRegCalendarAlt } from 'react-icons/fa'

import type { DatePickerType } from './datePicker.types'

interface DatePickerInputProps {
  pickerType: DatePickerType
  isRange?: boolean
  isDisabled?: boolean
  leftIcon?: ReactNode
  width?: string
  height?: string
  fontWeight?: string
  size?: 'small' | 'medium'
  onClick?: () => void
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onSelect?: (e: React.ChangeEvent<HTMLInputElement>) => void
}

export const DatePickerInput = forwardRef<HTMLInputElement, DatePickerInputProps>(
  (
    { pickerType, isDisabled, leftIcon, width, height, fontWeight, size = 'small', onClick, value, onChange, isRange, onKeyDown, onSelect },
    ref
  ) => {
    const inputWidths = {
      // We define specific widths instead of letting it be auto to avoid jumpy behaviour
      single: { day: '110px', week: '120px', month: '98px', quarter: '102px', year: '78px', datetime: '150px' },
      range: { day: '207px', week: '214px', month: '170px', quarter: '172px', year: '126px', datetime: '285px' },
    }
    const inputWidth = inputWidths[isRange ? 'range' : 'single'][pickerType]

    const inputHeight = size === 'medium' ? '40px' : '32px'
    const border = '1px solid border'
    const bgColor = 'white'
    const bgHoverColor = 'muted'
    const iconColor = isDisabled ? appColors.gray[400] : appColors.gray[800]

    return (
      <BaseInput
        ref={ref}
        onKeyDown={onKeyDown}
        isDisabled={isDisabled}
        opacity={isDisabled ? 0.5 : 1}
        width={width ?? inputWidth}
        variant='unstyled'
        height={height ?? inputHeight}
        label={''}
        placeholder={''}
        value={value}
        onChange={onChange}
        onClick={onClick}
        backgroundColor={bgColor}
        cursor='pointer'
        borderRadius='8px'
        border={border}
        _hover={{ backgroundColor: bgHoverColor, cursor: isDisabled ? 'not-allowed' : undefined }}
        _active={{ borderColor: 'primary' }}
        _focus={{ borderColor: 'primary' }}
        IconLeft={
          <Box mt='-2px' ml='6px'>
            {leftIcon ?? <FaRegCalendarAlt size={'16px'} color={iconColor} />}
          </Box>
        }
        fontSize='14px'
        fontWeight={fontWeight ?? 500}
        color='gray.800'
      />
    )
  }
)

DatePickerInput.displayName = 'DatePickerInput'
